// frontend/src/components/Financeiro/ContabilidadeSubmissoes.js
// =============================================
// SUBMISSÕES ÀS FINANÇAS
// ✅ Menu com KPIs, chips de filtro, tabela colorida por estado
// ✅ Modal com timeline, cards de valores e ações destacadas
// ✅ MOEDA E DATA DINÂMICAS (mesmo formato do Faturas.js)
// 🔥 SUPORTE AOS 3 REGIMES:
//    - Normal (IVA 16%): submissão MENSAL — mostra IVA Liquidado / Dedutível / a Entregar
//    - ISPC (15% Volume): submissão TRIMESTRAL — mostra Volume de Negócios / Taxa 15% / Imposto a Entregar
//    - Isento: sem submissão
// 🔥 KPIs, filtros, TABELA e MODAL adaptam-se ao regime da empresa
// 🔥 Deteção robusta de ISPC por 4 sinais: tipo, tipoImposto, taxaISPC/volumeNegocios, regimeEmpresa
// 🔥 Para regime Normal, TODOS os valores de IVA (Liquidado / Dedutível / a Entregar) são mostrados
// 🔥 DEBUG: log completo do fecho no console ao abrir o modal
// 🔥 NOVO: configuracao (moeda+formatacao) + empresa vêm diretamente do backend
//    (resposta de /api/contabilidade/fechos) — sem chamadas duplicadas
// 🔥 ALINHADO COM BACKEND:
//    - POST /fechos/:id/submeter  → submeterFinancas
//    - POST /fechos/:id/pagar     → marcarComoPago
//    - POST /fechos/:id/anular    → anularFecho
// 🔥 NOVO: Visualização de comprovativo em MODAL INTERNO (imagem ou PDF)
//    - Estilos 100% inline (não depende de CSS externo)
//    - 🔥 Portal para <body> para ficar SEMPRE acima do modal de detalhes
//    - 🔥 z-index 2147483002 (acima de --fin-modal-z = 2147483000)
//    - 🔥 className="fin-comprovativo-overlay" para estilização CSS opcional
//    - 🔥 Corrigido: hover do botão descarregar/fechar (borderRadius estava em JSX errado)
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Icon } from './SharedComponents';

const API = '/api' || 'http://localhost:3000';

const STATUS_BADGE = {
    'Pendente': 'warning',
    'Submetido': 'info',
    'Pago': 'success',
    'Isento': 'muted',
    'Anulado': 'danger'
};

const STATUS_ICON = {
    'Pendente': 'schedule',
    'Submetido': 'cloud_upload',
    'Pago': 'check_circle',
    'Isento': 'block',
    'Anulado': 'cancel'
};

const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MESES_ABREV = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const MESES_FIM_TRIMESTRE = [2, 5, 8, 11];

const trimestreDoMes = (mes) => Math.floor(mes / 3) + 1;

const TIPO_IMPOSTO_POR_REGIME = {
    Normal: 'IVA',
    ISPC: 'ISPC',
    Isento: 'Nenhum'
};

const PERIODICIDADE_POR_REGIME = {
    Normal: 'Mensal',
    ISPC: 'Trimestral',
    Isento: '—'
};

const TAXA_ISPC = 0.15;
const TAXA_ISPC_LABEL = '15%';

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    regimeIVA: 'Normal',
    percentagemIVA: 16,
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// 🔥 ESTILOS INLINE DO MODAL DE COMPROVATIVO
// =============================================
const S = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 🔥 Acima de --fin-modal-z (2147483000) do modal de detalhes
        zIndex: 2147483002,
        padding: '1rem'
    },
    modal: {
        background: '#ffffff',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 8px 20px rgba(0,0,0,0.15)'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #e5e7eb',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        flexShrink: 0
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        margin: 0,
        fontSize: '1.05rem',
        fontWeight: 700,
        color: '#1e293b',
        letterSpacing: '-0.01em'
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
    },
    downloadBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.5rem 0.9rem',
        background: '#4f46e5',
        color: '#ffffff',
        height: '38px',
        border: 'none',
        borderRadius: '20px',
        fontSize: '0.8rem',
        fontWeight: 600,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 6px rgba(79,70,229,0.25)'
    },
    closeBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        background: 'transparent',
        border: 'none',
        borderRadius: '18px',
        fontSize: '1.5rem',
        lineHeight: 1,
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    body: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        background:
            'linear-gradient(45deg, #f9fafb 25%, transparent 25%),' +
            'linear-gradient(-45deg, #f9fafb 25%, transparent 25%),' +
            'linear-gradient(45deg, transparent 75%, #f9fafb 75%),' +
            'linear-gradient(-45deg, transparent 75%, #f9fafb 75%)',
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
        backgroundColor: '#ffffff',
        minHeight: '400px',
        maxHeight: 'calc(90vh - 140px)'
    },
    img: {
        maxWidth: '100%',
        maxHeight: '75vh',
        objectFit: 'contain',
        borderRadius: '8px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.08)',
        background: '#ffffff'
    },
    pdf: {
        width: '100%',
        height: '75vh',
        border: 'none',
        borderRadius: '8px',
        background: '#ffffff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
    },
    erroBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        textAlign: 'center',
        color: '#ef4444',
        padding: '2rem'
    },
    erroTexto: {
        margin: 0,
        fontSize: '0.9rem',
        fontWeight: 600,
        color: '#64748b'
    },
    erroLink: {
        color: '#3b82f6',
        fontSize: '0.85rem',
        fontWeight: 600,
        textDecoration: 'underline'
    },
    outroBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        textAlign: 'center',
        padding: '2rem'
    },
    outroTexto: {
        margin: 0,
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#64748b'
    },
    outroBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: '0.75rem',
        padding: '0.6rem 1.1rem',
        background: '#4f46e5',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '0.85rem',
        fontWeight: 600,
        textDecoration: 'none',
        cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(79,70,229,0.25)'
    }
};

export default function ContabilidadeSubmissoes() {
    const [fechos, setFechos] = useState([]);
    const [ano, setAno] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [filtroEstado, setFiltroEstado] = useState('');

    // 🔥 Estados do modal de detalhes
    const [showModal, setShowModal] = useState(false);
    const [fechoSelecionado, setFechoSelecionado] = useState(null);
    const [processando, setProcessando] = useState(false);
    const [erroModal, setErroModal] = useState('');
    const [sucessoModal, setSucessoModal] = useState('');

    const [referenciaSubmissao, setReferenciaSubmissao] = useState('');
    const [dataPagamento, setDataPagamento] = useState('');
    const [motivoAnulacao, setMotivoAnulacao] = useState('');
    const [acaoAtiva, setAcaoAtiva] = useState(null);

    // 🔥 Estados do modal de comprovativo
    const [showComprovativo, setShowComprovativo] = useState(false);
    const [comprovativoUrl, setComprovativoUrl] = useState('');
    const [comprovativoTipo, setComprovativoTipo] = useState(''); // 'imagem' | 'pdf' | 'outro'
    const [comprovativoErro, setComprovativoErro] = useState(false);

    // 🔥 CONFIGURAÇÕES DA EMPRESA — começam com padrão
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    // =============================================
    // 🔥 DERIVADOS DO REGIME
    // =============================================
    const regimeIVA = empresaConfig.regimeIVA || 'Normal';
    const isNormal = regimeIVA === 'Normal';
    const isISPC = regimeIVA === 'ISPC';
    const isIsento = regimeIVA === 'Isento';

    const tipoImposto = TIPO_IMPOSTO_POR_REGIME[regimeIVA] || 'IVA';
    const periodicidadeLabel = PERIODICIDADE_POR_REGIME[regimeIVA] || 'Mensal';
    const taxaLabel = isNormal
        ? `${empresaConfig.percentagemIVA ?? 16}%`
        : isISPC
            ? `${TAXA_ISPC_LABEL} (trimestral)`
            : '—';

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    const fmt = formatarMoeda;

    // =============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // =============================================
    const formatarData = useCallback((data, comHora = false) => {
        if (!data) return '—';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '—';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mes);
        formato = formato.replace('YYYY', ano);
        formato = formato.replace('YY', String(ano).slice(-2));

        const deveIncluirHora = comHora || cfg.incluirHora;
        if (deveIncluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    const fmtData = formatarData;

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                regimeIVA: emp?.regimeIVA ?? prev.regimeIVA,
                percentagemIVA: emp?.percentagemIVA ?? prev.percentagemIVA,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // 🔥 CARREGAR FECHOS — devolve TODOS (Mensal + Trimestral)
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/contabilidade/fechos`, {
                params: { ano },
                headers
            });
            if (res.data.success) {
                aplicarConfiguracao(res.data);

                const lista = res.data.fechos || [];
                console.log('🔍 [Submissões] Fechos carregados:', lista.length, 'para ano', ano);
                setFechos(lista);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [ano, headers, aplicarConfiguracao]);

    useEffect(() => { carregar(); }, [carregar]);

    // =============================================
    // 🔥 HELPER — Detetar se um fecho é ISPC
    // =============================================
    const isFechoISPC = useCallback((fecho) => {
        if (!fecho) return false;

        if (regimeIVA === 'Normal') return false;
        if (regimeIVA === 'ISPC') return true;

        if (fecho.tipo === 'Trimestral') return true;
        if (fecho.tipoImposto === 'ISPC') return true;

        const ap = fecho.apuramentoIVA || {};
        if (ap.taxaISPC) return true;
        if (ap.volumeNegocios !== undefined && ap.volumeNegocios > 0) return true;

        return false;
    }, [regimeIVA]);

    // =============================================
    // 🔥 HELPER — Imposto a entregar por fecho
    // =============================================
    const getImpostoFecho = useCallback((fecho) => {
        if (!fecho) return 0;
        const ap = fecho.apuramentoIVA || {};
        const receitas = fecho.receitas || {};

        if (isFechoISPC(fecho)) {
            if (typeof ap.ivaAEntregar === 'number' && ap.ivaAEntregar > 0) {
                return ap.ivaAEntregar;
            }
            const volume = ap.volumeNegocios || receitas.baseTributavel || 0;
            return parseFloat((volume * TAXA_ISPC).toFixed(2));
        }

        return ap.ivaAEntregar || 0;
    }, [isFechoISPC]);

    // =============================================
    // 🔥 HELPER — Volume de Negócios por fecho
    // =============================================
    const getVolumeFecho = useCallback((fecho) => {
        if (!fecho) return 0;
        const ap = fecho.apuramentoIVA || {};
        const receitas = fecho.receitas || {};
        return ap.volumeNegocios
            || receitas.baseTributavel
            || receitas.totalFaturado
            || 0;
    }, []);

    // =============================================
    // 🔥 HELPER — Valores de IVA por fecho (regime Normal)
    // =============================================
    const getValoresIVA = useCallback((fecho) => {
        if (!fecho) {
            return { liquidado: 0, dedutivel: 0, aEntregar: 0, aRecuperar: 0 };
        }
        const ap = fecho.apuramentoIVA || {};
        return {
            liquidado: ap.ivaLiquidado || 0,
            dedutivel: ap.ivaDedutivel || 0,
            aEntregar: ap.ivaAEntregar || 0,
            aRecuperar: ap.ivaARecuperar || 0
        };
    }, []);

    // =============================================
    // 🔥 KPIs — adaptados ao regime
    // =============================================
    const kpis = useMemo(() => {
        const total = fechos.length;
        const pendentes = fechos.filter(f => (f.submissaoFinancas?.estado || 'Pendente') === 'Pendente').length;
        const submetidos = fechos.filter(f => f.submissaoFinancas?.estado === 'Submetido').length;
        const pagos = fechos.filter(f => f.submissaoFinancas?.estado === 'Pago').length;

        const impostoTotal = fechos.reduce((s, f) => s + getImpostoFecho(f), 0);
        const impostoPago = fechos
            .filter(f => f.submissaoFinancas?.estado === 'Pago')
            .reduce((s, f) => s + getImpostoFecho(f), 0);
        const impostoPendente = fechos
            .filter(f => {
                const est = f.submissaoFinancas?.estado || 'Pendente';
                return est !== 'Pago' && est !== 'Anulado';
            })
            .reduce((s, f) => s + getImpostoFecho(f), 0);

        return {
            total,
            pendentes,
            submetidos,
            pagos,
            impostoTotal,
            impostoPago,
            impostoPendente
        };
    }, [fechos, getImpostoFecho]);

    // =============================================
    // 🔥 FILTRAGEM POR CHIP
    // =============================================
    const fechosFiltrados = useMemo(() => {
        if (!filtroEstado) return fechos;
        return fechos.filter(f => {
            const estado = f.submissaoFinancas?.estado || 'Pendente';
            return estado === filtroEstado;
        });
    }, [fechos, filtroEstado]);

    // =============================================
    // MODAL — ABRIR/FECHAR
    // =============================================
    const abrirDetalhes = (fecho) => {
        setFechoSelecionado(fecho);
        setErroModal('');
        setSucessoModal('');
        setAcaoAtiva(null);
        setReferenciaSubmissao(fecho.submissaoFinancas?.referencia || '');
        setDataPagamento(
            fecho.submissaoFinancas?.dataPagamento
                ? new Date(fecho.submissaoFinancas.dataPagamento).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0]
        );
        setMotivoAnulacao('');
        setShowModal(true);
    };

    const fecharModal = () => {
        if (processando) return;
        setShowModal(false);
        setFechoSelecionado(null);
        setErroModal('');
        setSucessoModal('');
        setAcaoAtiva(null);
    };

    // =============================================
    // 🔥 HELPER — Recarregar fecho selecionado após ação
    // =============================================
    const recarregarFechoSelecionado = useCallback(async (idFecho) => {
        try {
            const res = await axios.get(
                `${API}/contabilidade/fechos/${idFecho}`,
                { headers }
            );
            if (res.data.success && res.data.fecho) {
                setFechoSelecionado(res.data.fecho);
            }
        } catch (e) {
            console.warn('⚠️ Não foi possível recarregar fecho:', e.message);
        }
    }, [headers]);

    // =============================================
    // AÇÕES
    // =============================================
    const submeter = async () => {
        if (!fechoSelecionado) return;
        const idFecho = fechoSelecionado._id;
        setErroModal(''); setSucessoModal(''); setProcessando(true);
        try {
            const res = await axios.post(
                `${API}/contabilidade/fechos/${idFecho}/submeter`,
                { referencia: referenciaSubmissao.trim() || null },
                { headers }
            );
            if (res.data.success) {
                setSucessoModal('Submissão registada com sucesso!');
                setAcaoAtiva(null);
                await carregar();
                await recarregarFechoSelecionado(idFecho);
            } else {
                setErroModal(res.data.message || res.data.erro || 'Erro ao submeter');
            }
        } catch (err) {
            setErroModal(err.response?.data?.message || err.response?.data?.erro || 'Erro ao submeter');
        } finally {
            setProcessando(false);
        }
    };

    const marcarPago = async () => {
        if (!fechoSelecionado) return;
        const idFecho = fechoSelecionado._id;
        setErroModal(''); setSucessoModal(''); setProcessando(true);
        try {
            const res = await axios.post(
                `${API}/contabilidade/fechos/${idFecho}/pagar`,
                { dataPagamento },
                { headers }
            );
            if (res.data.success) {
                setSucessoModal('Pagamento registado com sucesso!');
                setAcaoAtiva(null);
                await carregar();
                await recarregarFechoSelecionado(idFecho);
            } else {
                setErroModal(res.data.message || res.data.erro || 'Erro ao registar pagamento');
            }
        } catch (err) {
            setErroModal(err.response?.data?.message || err.response?.data?.erro || 'Erro ao registar pagamento');
        } finally {
            setProcessando(false);
        }
    };

    const anular = async () => {
        if (!fechoSelecionado) return;
        if (!motivoAnulacao.trim()) {
            setErroModal('Motivo da anulação é obrigatório');
            return;
        }
        const idFecho = fechoSelecionado._id;
        setErroModal(''); setSucessoModal(''); setProcessando(true);
        try {
            const res = await axios.post(
                `${API}/contabilidade/fechos/${idFecho}/anular`,
                { motivo: motivoAnulacao.trim() },
                { headers }
            );
            if (res.data.success) {
                setSucessoModal('Fecho anulado com sucesso!');
                setAcaoAtiva(null);
                await carregar();
                await recarregarFechoSelecionado(idFecho);
            } else {
                setErroModal(res.data.message || res.data.erro || 'Erro ao anular');
            }
        } catch (err) {
            setErroModal(err.response?.data?.message || err.response?.data?.erro || 'Erro ao anular');
        } finally {
            setProcessando(false);
        }
    };

    // =============================================
    // 🔥 VER COMPROVATIVO — abre modal interno
    // =============================================
    const verComprovativo = (url) => {
        if (!url) return;

        let urlFinal = url;

        if (url.startsWith('http://') || url.startsWith('https://')) {
            urlFinal = url;
        } else {
            let caminhoLimpo = url;
            if (caminhoLimpo.startsWith('/api')) {
                caminhoLimpo = caminhoLimpo.substring(4);
            }
            if (!caminhoLimpo.startsWith('/')) {
                caminhoLimpo = '/' + caminhoLimpo;
            }
            urlFinal = `${window.location.origin}${caminhoLimpo}`;
        }

        console.log('🔗 [Comprovativo] URL final:', urlFinal);

        const ext = urlFinal.split('.').pop().toLowerCase().split('?')[0];
        let tipo = 'outro';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
            tipo = 'imagem';
        } else if (ext === 'pdf') {
            tipo = 'pdf';
        }

        setComprovativoUrl(urlFinal);
        setComprovativoTipo(tipo);
        setComprovativoErro(false);
        setShowComprovativo(true);
    };

    const fecharComprovativo = () => {
        setShowComprovativo(false);
        setComprovativoUrl('');
        setComprovativoTipo('');
        setComprovativoErro(false);
    };

    // =============================================
    // CLASSE DA LINHA POR ESTADO
    // =============================================
    const rowClass = (estado) => {
        switch (estado) {
            case 'Pendente': return 'row-pendente';
            case 'Submetido': return 'row-submetido';
            case 'Pago': return 'row-pago';
            case 'Anulado': return 'row-anulado';
            default: return '';
        }
    };

    // =============================================
    // 🔥 HELPER — Label do período
    // =============================================
    const labelPeriodo = (fecho) => {
        if (!fecho) return '—';
        const fechoISPC = isFechoISPC(fecho);

        if (fechoISPC) {
            const tri = fecho.trimestre || trimestreDoMes(fecho.mes || 0);
            return `${fecho.ano} / T${tri}`;
        }
        return `${fecho.ano} / ${String((fecho.mes || 0) + 1).padStart(2, '0')}`;
    };

    const labelMesesPeriodo = (fecho) => {
        if (!fecho) return '';
        const fechoISPC = isFechoISPC(fecho);

        if (fechoISPC) {
            const tri = fecho.trimestre || trimestreDoMes(fecho.mes || 0);
            const mesesDoTri = {
                1: 'Jan, Fev, Mar',
                2: 'Abr, Mai, Jun',
                3: 'Jul, Ago, Set',
                4: 'Out, Nov, Dez'
            }[tri];
            return mesesDoTri;
        }
        return MESES[fecho.mes] || `Mês ${(fecho.mes || 0) + 1}`;
    };

    // =============================================
    // TIMELINE
    // =============================================
    const renderTimeline = (estado) => {
        const passos = [
            { key: 'Pendente', label: 'Pendente', icon: 'schedule' },
            { key: 'Submetido', label: 'Submetido', icon: 'cloud_upload' },
            { key: 'Pago', label: 'Pago', icon: 'check_circle' }
        ];
        const ordem = { 'Pendente': 0, 'Submetido': 1, 'Pago': 2, 'Anulado': -1 };
        const idx = ordem[estado] ?? 0;

        return (
            <div className="detail-timeline">
                {passos.map((p, i) => {
                    const done = i < idx;
                    const current = i === idx;
                    const cls = done ? 'done' : current ? 'current' : '';
                    return (
                        <div key={p.key} className={`detail-timeline-step ${cls}`}>
                            <div className="detail-timeline-dot">
                                {done ? '✓' : i + 1}
                            </div>
                            <span className="detail-timeline-label">{p.label}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    // =============================================
    // 🔥 RENDER MODAL DE DETALHES
    // =============================================
    const renderModalContent = () => {
        if (!fechoSelecionado) return null;
        const sub = fechoSelecionado.submissaoFinancas || {};
        const estado = sub.estado || 'Pendente';

        const podeSubmeter = estado === 'Pendente';
        const podePagar = estado === 'Submetido';
        const podeAnular = estado !== 'Pago' && estado !== 'Anulado';
        const temComprovativo = !!sub.comprovativoUrl;

        const ap = fechoSelecionado.apuramentoIVA || {};
        const receitas = fechoSelecionado.receitas || {};

        const fechoISPC = isFechoISPC(fechoSelecionado);

        const volumeNegocios = getVolumeFecho(fechoSelecionado);
        const impostoAEntregar = getImpostoFecho(fechoSelecionado);
        const valoresIVA = getValoresIVA(fechoSelecionado);

        console.log('🔍 [Submissões] Modal fecho:', {
            id: fechoSelecionado._id,
            mes: fechoSelecionado.mes,
            tipo: fechoSelecionado.tipo,
            regimeEmpresa: regimeIVA,
            isFechoISPC: fechoISPC,
            volumeCalculado: volumeNegocios,
            impostoCalculado: impostoAEntregar
        });

        return (
            <div className="modal-body">
                {erroModal && (
                    <div className="alert alert-danger">
                        <Icon name="error" size={16} />
                        <span>{erroModal}</span>
                    </div>
                )}
                {sucessoModal && (
                    <div className="alert alert-success">
                        <Icon name="check_circle" size={16} />
                        <span>{sucessoModal}</span>
                    </div>
                )}

                {renderTimeline(estado)}

                <div className="detail-section">
                    <h4 className="detail-section-title">
                        <Icon name="calendar_today" size={14} />
                        Período & Referência
                    </h4>

                    <div className="detail-pills">
                        <span className="detail-pill">
                            <Icon name="event" size={12} />
                            <strong>{labelPeriodo(fechoSelecionado)}</strong>
                            {' — '}{labelMesesPeriodo(fechoSelecionado)}
                        </span>
                        <span className="detail-pill">
                            <Icon name="lock_clock" size={12} />
                            Fechado em <strong>{fmtData(fechoSelecionado.fechadoEm, true)}</strong>
                        </span>
                        {sub.referencia && (
                            <span className="detail-pill">
                                <Icon name="tag" size={12} />
                                Ref. <strong>{sub.referencia}</strong>
                            </span>
                        )}
                        {sub.dataPagamento && (
                            <span className="detail-pill">
                                <Icon name="payments" size={12} />
                                Pago em <strong>{fmtData(sub.dataPagamento)}</strong>
                            </span>
                        )}
                    </div>
                </div>

                <div className="detail-section">
                    <h4 className="detail-section-title">
                        <Icon name="receipt_long" size={14} />
                        {fechoISPC ? 'Apuramento ISPC' : 'Apuramento de IVA'}
                    </h4>

                    {fechoISPC ? (
                        <div className="detail-valores-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                            <div className="detail-valor-card" style={{ border: '2px solid #3b82f6' }}>
                                <div className="card-header">
                                    <Icon name="trending_up" size={14} color="#3b82f6" />
                                    <span className="card-label">Volume de Negócios</span>
                                </div>
                                <span className="card-value" style={{ color: '#2563eb' }}>
                                    {fmt(volumeNegocios)}
                                </span>
                            </div>

                            <div className="detail-valor-card" style={{ border: '2px solid #f59e0b' }}>
                                <div className="card-header">
                                    <Icon name="percent" size={14} color="#f59e0b" />
                                    <span className="card-label">Taxa ISPC</span>
                                </div>
                                <span className="card-value" style={{ color: '#d97706' }}>
                                    {TAXA_ISPC_LABEL}
                                </span>
                            </div>

                            <div className="detail-valor-card" style={{ border: '2px solid #10b981' }}>
                                <div className="card-header">
                                    <Icon name="account_balance" size={14} color="#10b981" />
                                    <span className="card-label">Imposto a Entregar</span>
                                </div>
                                <span className="card-value" style={{ color: '#10b981' }}>
                                    {fmt(impostoAEntregar)}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="detail-valores-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                            <div className="detail-valor-card liquidado">
                                <div className="card-header">
                                    <Icon name="trending_up" size={14} />
                                    <span className="card-label">IVA Liquidado</span>
                                </div>
                                <span className="card-value">
                                    {fmt(valoresIVA.liquidado)}
                                </span>
                            </div>

                            <div className="detail-valor-card dedutivel">
                                <div className="card-header">
                                    <Icon name="trending_down" size={14} />
                                    <span className="card-label">IVA Dedutível</span>
                                </div>
                                <span className="card-value">
                                    {fmt(valoresIVA.dedutivel)}
                                </span>
                            </div>

                            <div className="detail-valor-card entregar">
                                <div className="card-header">
                                    <Icon name="account_balance" size={14} />
                                    <span className="card-label">IVA a Entregar</span>
                                </div>
                                <span className="card-value">
                                    {fmt(valoresIVA.aEntregar)}
                                </span>
                            </div>
                        </div>
                    )}

                    {!fechoISPC && valoresIVA.aRecuperar > 0 && (
                        <div style={{
                            marginTop: '0.75rem',
                            padding: '0.6rem 0.85rem',
                            background: 'rgba(139, 92, 246, 0.08)',
                            border: '2px solid rgba(139, 92, 246, 0.2)',
                            borderRadius: 'var(--fin-radius-sm)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#7c3aed'
                        }}>
                            <Icon name="trending_down" size={16} color="#7c3aed" />
                            <span>
                                IVA a Recuperar:{' '}
                                <strong>{fmt(valoresIVA.aRecuperar)}</strong>
                            </span>
                        </div>
                    )}
                </div>

                {temComprovativo && (
                    <div className="detail-section">
                        <h4 className="detail-section-title">
                            <Icon name="description" size={14} />
                            Comprovativo
                        </h4>
                        <button
                            className="detail-comprovativo-btn"
                            onClick={() => verComprovativo(sub.comprovativoUrl)}
                        >
                            <Icon name="visibility" size={16} />
                            Ver comprovativo
                        </button>
                    </div>
                )}

                {(podeSubmeter || podePagar || podeAnular) && (
                    <div className="detail-section">
                        <h4 className="detail-section-title">
                            <Icon name="settings" size={14} />
                            Ações disponíveis
                        </h4>

                        {!acaoAtiva && (
                            <div className="detail-actions-grid">
                                {podeSubmeter && (
                                    <button style={{ border: '2px solid #3b82f6' }}
                                        className="detail-action-btn primary"
                                        onClick={() => setAcaoAtiva('submeter')}
                                    >
                                        <div className="btn-icon">
                                            <Icon name="cloud_upload" size={18} color="#fff" />
                                        </div>
                                        <div className="btn-info">
                                            <span className="btn-title">Submeter às Finanças</span>
                                            <span className="btn-desc">
                                                {fechoISPC ? 'Registar submissão trimestral' : 'Registar submissão mensal'}
                                            </span>
                                        </div>
                                    </button>
                                )}

                                {podePagar && (
                                    <button style={{ border: '2px solid #10b981' }}
                                        className="detail-action-btn success"
                                        onClick={() => setAcaoAtiva('pagar')}
                                    >
                                        <div className="btn-icon">
                                            <Icon name="payments" size={18} color="#fff" />
                                        </div>
                                        <div className="btn-info">
                                            <span className="btn-title">Marcar como Pago</span>
                                            <span className="btn-desc">
                                                {fechoISPC ? 'Registar pagamento do ISPC' : 'Registar pagamento do IVA'}
                                            </span>
                                        </div>
                                    </button>
                                )}

                                {podeAnular && (
                                    <button style={{ border: '2px solid #ef4444' }}
                                        className="detail-action-btn danger"
                                        onClick={() => setAcaoAtiva('anular')}
                                    >
                                        <div className="btn-icon" >
                                            <Icon name="cancel" size={18} color="#fff" />
                                        </div>
                                        <div className="btn-info">
                                            <span className="btn-title">Anular Fecho</span>
                                            <span className="btn-desc">Cancelar este período</span>
                                        </div>
                                    </button>
                                )}
                            </div>
                        )}

                        {acaoAtiva === 'submeter' && (
                            <div className="detail-form">
                                <label>Referência da submissão (opcional)</label>
                                <input
                                    type="text"
                                    value={referenciaSubmissao}
                                    onChange={e => setReferenciaSubmissao(e.target.value)}
                                    placeholder={fechoISPC ? 'Ex: ISPC-2026-T3' : 'Ex: 2026/09/12345'}
                                    disabled={processando}
                                />
                                <div className="detail-form-actions">
                                    <button className="fin-btn-primary" onClick={submeter} disabled={processando}>
                                        {processando ? 'A submeter...' : 'Confirmar Submissão'}
                                    </button>
                                    <button className="fin-btn-secondary" onClick={() => setAcaoAtiva(null)} disabled={processando}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}

                        {acaoAtiva === 'pagar' && (
                            <div className="detail-form">
                                <label>Data de pagamento</label>
                                <input
                                    type="date"
                                    value={dataPagamento}
                                    onChange={e => setDataPagamento(e.target.value)}
                                    disabled={processando}
                                />
                                <div className="detail-form-actions">
                                    <button className="fin-btn-primary" onClick={marcarPago} disabled={processando}>
                                        {processando ? 'A registar...' : 'Confirmar Pagamento'}
                                    </button>
                                    <button className="fin-btn-secondary" onClick={() => setAcaoAtiva(null)} disabled={processando}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}

                        {acaoAtiva === 'anular' && (
                            <div className="detail-form">
                                <label>Motivo da anulação <span className="required">*</span></label>
                                <textarea
                                    value={motivoAnulacao}
                                    onChange={e => setMotivoAnulacao(e.target.value)}
                                    placeholder="Descreve o motivo..."
                                    rows={3}
                                    disabled={processando}
                                />
                                <div className="detail-form-actions">
                                    <button className="fin-btn-danger" onClick={anular} disabled={processando}>
                                        {processando ? 'A anular...' : 'Confirmar Anulação'}
                                    </button>
                                    <button className="fin-btn-secondary" onClick={() => setAcaoAtiva(null)} disabled={processando}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {estado === 'Pago' && (
                    <div className="detail-info-success">
                        <Icon name="check_circle" size={16} />
                        Este fecho já foi pago às Finanças.
                    </div>
                )}
                {estado === 'Anulado' && (
                    <div className="detail-info-danger">
                        <Icon name="cancel" size={16} />
                        Este fecho foi anulado.
                    </div>
                )}
            </div>
        );
    };

    // =============================================
    // 🔥 RENDER MODAL DE COMPROVATIVO (PORTAL + z-index alto)
    // =============================================
    const renderComprovativoModal = () => {
        if (!showComprovativo) return null;

        const conteudo = (
            <div
                style={S.overlay}
                className="fin-comprovativo-overlay"
                onClick={fecharComprovativo}
            >
                <div
                    style={S.modal}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div style={S.header}>
                        <h3 style={S.title}>
                            <Icon name="description" size={20} color="#4f46e5" />
                            Comprovativo
                        </h3>
                        <div style={S.actions}>
                            <a
                                href={comprovativoUrl}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                style={S.downloadBtn}
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = '#4338ca';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(79,70,229,0.35)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = '#4f46e5';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(79,70,229,0.25)';
                                }}
                            >
                                <Icon name="download" size={16} />
                                Descarregar
                            </a>
                            <button
                                style={S.closeBtn}
                                onClick={fecharComprovativo}
                                title="Fechar"
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = '#fee2e2';
                                    e.currentTarget.style.color = '#ef4444';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = '#64748b';
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div style={S.body}>
                        {comprovativoErro ? (
                            <div style={S.erroBox}>
                                <Icon name="error" size={48} color="#ef4444" />
                                <p style={S.erroTexto}>
                                    Não foi possível carregar o comprovativo.
                                </p>
                                <a
                                    href={comprovativoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={S.erroLink}
                                >
                                    Tentar abrir diretamente
                                </a>
                            </div>
                        ) : comprovativoTipo === 'imagem' ? (
                            <img
                                src={comprovativoUrl}
                                alt="Comprovativo"
                                style={S.img}
                                onError={() => setComprovativoErro(true)}
                            />
                        ) : comprovativoTipo === 'pdf' ? (
                            <iframe
                                src={comprovativoUrl}
                                title="Comprovativo PDF"
                                style={S.pdf}
                                onError={() => setComprovativoErro(true)}
                            />
                        ) : (
                            <div style={S.outroBox}>
                                <Icon name="description" size={48} color="#94a3b8" />
                                <p style={S.outroTexto}>
                                    Pré-visualização não disponível para este tipo de ficheiro.
                                </p>
                                <a
                                    href={comprovativoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={S.outroBtn}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.background = '#4338ca';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = '#4f46e5';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <Icon name="open_in_new" size={16} />
                                    Abrir ficheiro
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );

        // 🔥 Portal para <body> — o modal do comprovativo passa a viver
        // fora da árvore do modal de detalhes e o z-index aplica-se ao
        // nível do <body>, ficando SEMPRE por cima.
        return createPortal(conteudo, document.body);
    };

    // =============================================
    // RENDER PRINCIPAL
    // =============================================
    return (
        <div className="fin-submissoes">
            <header className="fin-section-header">
                <div className="fin-section-title">
                    <Icon name="cloud_upload" size={24} color="var(--fin-primary)" />
                    <div>
                        <h2>Submissões às Finanças</h2>
                        <p>
                            {fechos.length} fecho(s) no ano {ano}
                            {isISPC && <> · Regime ISPC (trimestral)</>}
                            {isNormal && <> · Regime Normal (IVA {taxaLabel})</>}
                        </p>
                    </div>
                </div>
                <div className="fin-section-actions">
                    <select value={ano} onChange={e => setAno(parseInt(e.target.value))} className="fin-filter-select">
                        {[2023, 2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <button className="fin-btn-refresh" onClick={carregar}>
                        <Icon name="refresh" size={16} color="#fff" />
                    </button>
                </div>
            </header>

            {!loading && fechos.length > 0 && (
                <div className="fin-kpi-grid">
                    <div className="fin-kpi-card primary" style={{ border: '2px solid #4f46e5' }}>
                        <div className="fin-kpi-icon">
                            <Icon name="account_balance" size={18} />
                        </div>
                        <div className="fin-kpi-info">
                            <span className="fin-kpi-value">{fmt(kpis.impostoTotal)}</span>
                            <span className="fin-kpi-label">
                                {isISPC ? 'ISPC Total a Entregar' : 'IVA Total a Entregar'}
                            </span>
                        </div>
                    </div>

                    <div className="fin-kpi-card warning" style={{ border: '2px solid #f59e0b' }}>
                        <div className="fin-kpi-icon">
                            <Icon name="hourglass_top" size={18} />
                        </div>
                        <div className="fin-kpi-info">
                            <span className="fin-kpi-value">{fmt(kpis.impostoPendente)}</span>
                            <span className="fin-kpi-label">
                                {isISPC ? 'ISPC Pendente' : 'IVA Pendente'}
                            </span>
                        </div>
                    </div>

                    <div className="fin-kpi-card success" style={{ border: '2px solid #10b981' }}>
                        <div className="fin-kpi-icon">
                            <Icon name="check_circle" size={18} />
                        </div>
                        <div className="fin-kpi-info">
                            <span className="fin-kpi-value">{fmt(kpis.impostoPago)}</span>
                            <span className="fin-kpi-label">
                                {isISPC ? 'ISPC Pago' : 'IVA Pago'}
                            </span>
                        </div>
                    </div>

                    <div className="fin-kpi-card info" style={{ border: '2px solid #3b82f6' }}>
                        <div className="fin-kpi-icon">
                            <Icon name="event_note" size={18} />
                        </div>
                        <div className="fin-kpi-info">
                            <span className="fin-kpi-value">{kpis.total}</span>
                            <span className="fin-kpi-label">Fechos no Ano</span>
                        </div>
                    </div>
                </div>
            )}

            {!loading && fechos.length > 0 && (
                <div className="fin-status-filters">
                    <button
                        className={`fin-filter-chip ${filtroEstado === '' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado('')}
                    >
                        Todos
                        <span className="chip-count">{fechos.length}</span>
                    </button>
                    <button
                        className={`fin-filter-chip ${filtroEstado === 'Pendente' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado('Pendente')}
                    >
                        Pendentes
                        <span className="chip-count">{kpis.pendentes}</span>
                    </button>
                    <button
                        className={`fin-filter-chip ${filtroEstado === 'Submetido' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado('Submetido')}
                    >
                        Submetidos
                        <span className="chip-count">{kpis.submetidos}</span>
                    </button>
                    <button
                        className={`fin-filter-chip ${filtroEstado === 'Pago' ? 'active' : ''}`}
                        onClick={() => setFiltroEstado('Pago')}
                    >
                        Pagos
                        <span className="chip-count">{kpis.pagos}</span>
                    </button>
                </div>
            )}

            {loading ? (
                <div className="fin-loading"><div className="fin-spinner" />A carregar...</div>
            ) : fechosFiltrados.length === 0 ? (
                <div className="fin-empty">
                    <Icon name="cloud_upload" size={48} color="var(--fin-muted)" />
                    <h3>Sem fechos</h3>
                    <p>
                        {filtroEstado
                            ? `Não há fechos com estado "${filtroEstado}".`
                            : `Nenhum fecho registado para ${ano}.`}
                    </p>
                </div>
            ) : (
                <div className="fin-table-wrapper">
                    <table className="fin-table">
                        <thead>
                            <tr>
                                <th>Período</th>
                                <th>Fechado em</th>
                                {isISPC ? (
                                    <>
                                        <th>Volume de Negócios</th>
                                        <th>Taxa ISPC</th>
                                        <th>Imposto a Entregar</th>
                                    </>
                                ) : (
                                    <>
                                        <th>IVA Liquidado</th>
                                        <th>IVA Dedutível</th>
                                        <th>IVA a Entregar</th>
                                    </>
                                )}
                                <th>Estado</th>
                                <th>Comprovativo</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fechosFiltrados.map(f => {
                                const sub = f.submissaoFinancas || {};
                                const estado = sub.estado || 'Pendente';
                                const corBadge = STATUS_BADGE[estado] || 'muted';
                                const iconBadge = STATUS_ICON[estado] || 'info';

                                const valoresIVA = getValoresIVA(f);

                                const fechoISPC = isFechoISPC(f);
                                const volumeNegocios = getVolumeFecho(f);
                                const impostoAEntregar = getImpostoFecho(f);

                                return (
                                    <tr key={f._id} className={rowClass(estado)}>
                                        <td>
                                            <strong>{labelPeriodo(f)}</strong>
                                        </td>
                                        <td>{fmtData(f.fechadoEm)}</td>
                                        {fechoISPC ? (
                                            <>
                                                <td>{fmt(volumeNegocios)}</td>
                                                <td>
                                                    <strong style={{ color: '#d97706' }}>
                                                        {TAXA_ISPC_LABEL}
                                                    </strong>
                                                </td>
                                                <td>
                                                    <span className="valor-destaque">
                                                        {fmt(impostoAEntregar)}
                                                    </span>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{fmt(valoresIVA.liquidado)}</td>
                                                <td>{fmt(valoresIVA.dedutivel)}</td>
                                                <td>
                                                    <span className="valor-destaque">
                                                        {fmt(valoresIVA.aEntregar)}
                                                    </span>
                                                </td>
                                            </>
                                        )}
                                        <td>
                                            <span className={`fin-badge badge-${corBadge}`}>
                                                <Icon name={iconBadge} size={11} />
                                                {estado}
                                            </span>
                                        </td>
                                        <td>
                                            {sub.comprovativoUrl ? (
                                                <button
                                                    className="fin-action-btn"
                                                    onClick={() => verComprovativo(sub.comprovativoUrl)}
                                                    title="Ver comprovativo"
                                                >
                                                    <Icon name="description" size={16} />
                                                </button>
                                            ) : (
                                                <span style={{ color: 'var(--fin-info-dark)', fontSize: '0.7rem' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                className="fin-action-btn"
                                                onClick={() => abrirDetalhes(f)}
                                                title="Ver detalhes"
                                            >
                                                <Icon name="visibility" size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && fechoSelecionado && (
                <div className="modal-overlay" onClick={() => !processando && fecharModal()}>
                    <div
                        className="modal-content modal-confirm"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <h3>
                                <Icon name="receipt_long" size={20} />
                                Detalhes do Fecho — {labelPeriodo(fechoSelecionado)}
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => !processando && fecharModal()}
                            >
                                ×
                            </button>
                        </div>

                        {renderModalContent()}

                        <div className="modal-actions">
                            <button
                                type="button"
                                className="modal-btn-secondary"
                                onClick={fecharModal}
                                disabled={processando}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 MODAL DE COMPROVATIVO — via Portal para <body> */}
            {renderComprovativoModal()}
        </div>
    );
}